import { ElementType } from 'react';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { PolymorphicComponentPropWithoutRef } from '@frontend/design-system';

type ThreadSubHeaderProps<E extends ElementType = 'div'> = PolymorphicComponentPropWithoutRef<E>;

/**
 * `ThreadSubHeader` wraps the secondary content of the header. It mainly controls the layout of that secondary section of
 * the header.
 * It is intended to be used for the thread phone number selectors and other secondary actions.
 */
export const ThreadSubHeader = <E extends ElementType = 'div'>({ as, children, ...rest }: ThreadSubHeaderProps<E>) => {
  const Component = as || 'div';
  const isSmallMode = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  return (
    <Component
      css={{
        display: 'flex',
        alignItems: 'center',
        padding: 'var(--fabric-space-padding-xxs) var(--fabric-space-padding-md)',
        position: 'relative',
        width: '100%',
        height: 'var(--fabric-number-n-10)',
        gap: isSmallMode ? theme.spacing(0.5) : 'var(--fabric-space-gap-sm)',
        borderTop: `1px solid ${theme.colors.neutral20}`,
        flexWrap: 'wrap',
        overflowX: 'hidden',
      }}
      {...rest}
    >
      {children}
    </Component>
  );
};
